import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  Badge,
  Box,
  Button,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  ErrorBoundary,
  Icon,
  Text,
  useAlert
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"

import {
  type EditorConfig,
  throwConfigError,
  useEditorConfig
} from "../../../../../EditorConfig"
import { tagSchema, updateTagCache, useTagSearch } from "../../../hooks/tagData"
import { useMediaLibrary } from "../../MediaLibraryContext"

export function Tags() {
  const { setTagManagerOpen } = useMediaLibrary()
  const [tagSearch, setTagSearch] = useState("")

  return (
    <Box flexDirection="column">
      <Box justifyContent="space-between">
        <Text color="grey12" variant="bold">
          Tags
        </Text>
        <Button
          startIcon="edit"
          invisible
          color="dark"
          onClick={() => setTagManagerOpen(true)}
        >
          Manage tags
        </Button>
      </Box>
      <ComboBox sx={{ marginTop: 4, marginBottom: 8 }}>
        <ComboBoxInput
          value={tagSearch}
          onValueChange={setTagSearch}
          placeholder="Add or create a new tag"
        />
        <ComboBoxContent side="top">
          <ErrorBoundary>
            <Suspense>
              <TagList searchTerm={tagSearch} setSearchTerm={setTagSearch} />
            </Suspense>
          </ErrorBoundary>
        </ComboBoxContent>
      </ComboBox>
      <Box flexDirection="column" alignItems="flex-start" gap={8}>
        {mockTags.map((tag) => (
          <Badge
            key={tag.id}
            title={tag.name}
            size="medium"
            color="purple"
            // TODO: Handle deleting tags from media item - PBD-1459
            onClose={() => {}}
          />
        ))}
      </Box>
    </Box>
  )
}

interface TagListProps {
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
}

function TagList(props: TagListProps) {
  const { searchTerm, setSearchTerm } = props

  const { items: tags } = useTagSearch()

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (!filteredTags.length)
    return <EmptyList searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

  return (
    <>
      {filteredTags.map((tag) => (
        // TODO: Add tag to media onSelect - PBD-1457
        <ComboBoxItem key={tag.id} onSelect={() => {}}>
          <Box padding={6}>
            <Badge title={tag.name} color="purple" size="medium" />
          </Box>
        </ComboBoxItem>
      ))}
    </>
  )
}

interface EmptyListProps {
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
}

function EmptyList(props: EmptyListProps) {
  const { searchTerm, setSearchTerm } = props

  const config = useEditorConfig()
  const { showAlert } = useAlert()

  function onCreateTag() {
    if (!searchTerm) return

    createTag({ name: searchTerm, config })
      .then((response) => {
        updateTagCache((tags) => [response, ...tags])
        setSearchTerm("")
      })
      .catch(() => {
        showAlert({
          title: "Error",
          subtitle: `Something went wrong when creating the tag ${searchTerm}`
        })
      })
  }

  if (!searchTerm) {
    return (
      <Text
        color="grey9"
        variant="small"
        sx={{ marginBlock: 8, marginInline: 8 }}
      >
        No tags found
      </Text>
    )
  }

  return (
    <>
      <Text
        color="grey9"
        variant="small"
        sx={{ marginBlock: 8, marginInline: 8 }}
      >
        No tags found
      </Text>
      <ComboBoxItem onSelect={onCreateTag}>
        <Box padding={8} flexDirection="row" alignItems="center" gap={4}>
          <Icon name="add" size="small" color="grey11" />
          <Text color="grey12">Create tag "{searchTerm}"</Text>
        </Box>
      </ComboBoxItem>
    </>
  )
}

const mockTags = [
  { id: "1", name: "Workshop" },
  { id: "2", name: "Conference" },
  { id: "3", name: "Event" }
]

interface CreateTagArgs {
  name: string
  config: EditorConfig
}

function createTag(args: CreateTagArgs) {
  const { name, config } = args

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL("tags", assetsApiBaseUrl), {
    headers: { repository },
    body: { name },
    method: "POST",
    credentials: getRequestCredentials(authStrategy),
    okType: tagSchema
  })
}
